import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CustomCursor from '../../components/CustomCursor';

const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'team', label: 'Team' },
  { id: 'problem', label: 'The Problem' },
  { id: 'vision', label: 'Product Vision' },
  { id: 'score-system', label: 'HumanMarq Score' },
  { id: 'seller-experience', label: 'Platform Policy' },
  { id: 'impact', label: 'Business Impact' },
  { id: 'results', label: 'Results' },
  { id: 'reflection', label: 'Reflection' },
];

function SideNav() {
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 120;
      let current = 'overview';
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el && el.offsetTop <= scrollY) {
          current = section.id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
    }
  };

  return (
    <nav className="hidden xl:flex flex-col gap-1 fixed left-8 top-1/2 -translate-y-1/2 z-40 w-[160px]">
      {sections.map((s) => (
        <button
          key={s.id}
          onClick={() => scrollTo(s.id)}
          className={`text-left text-[13px] px-3 py-1.5 rounded-md transition-all cursor-pointer whitespace-nowrap ${
            activeSection === s.id
              ? 'text-notion-accent font-medium bg-notion-accent/10'
              : 'text-notion-secondary hover:text-notion-text hover:bg-notion-hover'
          }`}
        >
          {s.label}
        </button>
      ))}
    </nav>
  );
}

export default function HumanMarqAIPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBackToPortfolio = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const portfolioSection = document.getElementById('portfolio');
      if (portfolioSection) {
        portfolioSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white font-sans cursor-none">
      <CustomCursor />
      <SideNav />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-notion-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBackToPortfolio}
              className="flex items-center gap-2 text-[14px] text-notion-secondary hover:text-notion-text transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              <span>Back to Portfolio</span>
            </button>
            <Link
              to="/"
              className="font-serif text-[18px] font-medium text-notion-text hover:text-notion-accent transition-colors cursor-pointer"
            >
              annie
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 lg:px-0">
        {/* Hero / Overview */}
        <section id="overview" className="pt-12 lg:pt-16 mb-16">
          <div className="flex items-center gap-2 mb-6">
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              Product Design
            </span>
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              AI Detection
            </span>
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              2026
            </span>
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl font-medium text-notion-text leading-tight tracking-tight mb-6">
            HumanMarq
          </h1>
          <p className="text-[17px] text-notion-secondary leading-relaxed mb-4">
            The world's first rating system that reveals the percentage likelihood that artwork was created by human hands rather than AI algorithms.
          </p>
          <p className="text-[17px] text-notion-text leading-relaxed mb-10 font-medium">
            An industry-first transparency system protecting collectors from AI deception as synthetic art threatens the $1B+ comic art market.
          </p>

          {/* Hero Image */}
          <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/09b2e855b8366559d31cbd26dbde2adf.png"
              alt="HumanMarq AI Launch Asset"
              title="HumanMarq AI Launch - Raremarq"
              className="w-full h-auto object-contain"
            />
          </div>
        </section>

        {/* Project Details */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[12px] font-medium text-notion-secondary mb-2 uppercase tracking-wide">
                My Role
              </h3>
              <p className="text-[15px] text-notion-text font-medium">Founding UX & Ops Designer</p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[12px] font-medium text-notion-secondary mb-2 uppercase tracking-wide">
                Timeline
              </h3>
              <p className="text-[15px] text-notion-text font-medium">February – March 2026</p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[12px] font-medium text-notion-secondary mb-2 uppercase tracking-wide">
                Tools
              </h3>
              <p className="text-[15px] text-notion-text font-medium">Figma</p>
            </div>
          </div>
        </section>

        {/* Team */}
        <section id="team" className="mb-16">
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8">
            <h3 className="text-[12px] font-medium text-notion-secondary mb-5 uppercase tracking-wide">
              Team
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { initials: 'CT', name: 'Christopher Tung', role: 'Founder' },
                { initials: 'AD', name: 'Annie Ding', role: 'Founding UX & Ops Designer' },
                { initials: 'JS', name: 'Jacob Singer', role: 'Co-Founder and CTO' },
                { initials: 'JM', name: 'Jon', role: 'Marketing Manager' },
              ].map((m) => (
                <div key={m.name} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-notion-accent/15 flex items-center justify-center flex-shrink-0">
                    <span className="text-notion-accent font-medium text-sm">{m.initials}</span>
                  </div>
                  <div>
                    <p className="text-[14px] font-medium text-notion-text">{m.name}</p>
                    <p className="text-[12px] text-notion-secondary">{m.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The Problem */}
        <section id="problem" className="mb-20">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-8">
            The Crisis: AI Art's Silent Invasion
          </h2>
          
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            As generative AI tools became increasingly sophisticated, collectors investing thousands—sometimes tens of thousands—of dollars in comic art faced a troubling reality: they often couldn't tell if what they were buying represented months of human skill or minutes of AI prompting.
          </p>

          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            The line between human creativity and algorithmic output had become dangerously blurred. Collectors could no longer visually distinguish between:
          </p>

          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-notion-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-brush-line text-notion-accent text-xs"></i>
                </span>
                <span className="text-[15px] text-notion-text">Fully human-created artwork</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-notion-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-palette-line text-notion-accent text-xs"></i>
                </span>
                <span className="text-[15px] text-notion-text">AI-assisted work</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-notion-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-robot-line text-notion-accent text-xs"></i>
                </span>
                <span className="text-[15px] text-notion-text">Predominantly AI-generated pieces</span>
              </li>
            </ul>
          </div>

          <p className="text-[16px] text-notion-secondary leading-relaxed italic mb-8">
            "We're witnessing an authenticity crisis. AI-generated art is flooding marketplaces, and collectors are unknowingly paying premium prices for work that was never touched by human hands. This isn't just about technology—it's about protecting the value of human creativity itself." — Chris Tung, Co-Founder of Raremarq
          </p>

          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            This created three major risks:
          </p>

          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            We had examples of AI art appearing on Raremarq. Multiple artists reached out directly, urging us to take action to protect the integrity of the platform and their work.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-5">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mb-3">
                <i className="ri-arrow-down-line text-red-600 text-sm"></i>
              </div>
              <h4 className="text-[14px] font-medium text-notion-text mb-2">Devaluation</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                Original comic art losing value
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-5">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mb-3">
                <i className="ri-shield-cross-line text-red-600 text-sm"></i>
              </div>
              <h4 className="text-[14px] font-medium text-notion-text mb-2">Trust Erosion</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                Buyer confidence declining
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-5">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mb-3">
                <i className="ri-alert-line text-red-600 text-sm"></i>
              </div>
              <h4 className="text-[14px] font-medium text-notion-text mb-2">Reputation Risk</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                Platform credibility at stake
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 text-notion-text rounded-lg p-6">
            <p className="text-[16px] font-medium leading-relaxed text-center">
              Category defense, not just feature launch.
            </p>
          </div>
        </section>

        {/* Product Vision */}
        <section id="vision" className="mb-20">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-8">
            The Solution: HumanMarq Changes Everything
          </h2>
          
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            Instead of quietly moderating AI content behind the scenes, we made authenticity visible. Raremarq became the industry's first major platform to actively combat the flood of AI-generated content threatening to devalue authentic human artistry.
          </p>

          <p className="text-[16px] text-notion-secondary leading-relaxed mb-8">
            The HumanMarq rating system evaluates every artwork listed on Raremarq through a proprietary multi-signal analysis:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <div className="w-10 h-10 rounded-full bg-notion-accent/10 flex items-center justify-center mb-4">
                <i className="ri-file-list-line text-notion-accent text-lg"></i>
              </div>
              <h4 className="text-[15px] font-medium text-notion-text mb-2">Process Transparency</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                Artistic process transparency and documentation
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <div className="w-10 h-10 rounded-full bg-notion-accent/10 flex items-center justify-center mb-4">
                <i className="ri-search-eye-line text-notion-accent text-lg"></i>
              </div>
              <h4 className="text-[15px] font-medium text-notion-text mb-2">Forensic Analysis</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                Forensic stylistic analysis
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <div className="w-10 h-10 rounded-full bg-notion-accent/10 flex items-center justify-center mb-4">
                <i className="ri-database-line text-notion-accent text-lg"></i>
              </div>
              <h4 className="text-[15px] font-medium text-notion-text mb-2">Metadata Review</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                Deep metadata review
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <div className="w-10 h-10 rounded-full bg-notion-accent/10 flex items-center justify-center mb-4">
                <i className="ri-user-voice-line text-notion-accent text-lg"></i>
              </div>
              <h4 className="text-[15px] font-medium text-notion-text mb-2">Artist Disclosure</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                Direct artist disclosure protocols
              </p>
            </div>
          </div>

          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6 mb-8">
            <p className="text-[15px] text-notion-text leading-relaxed mb-3">
              Each piece receives a clear rating along a spectrum, giving collectors unprecedented visibility into what they're actually buying.
            </p>
            <p className="text-[15px] text-notion-text font-medium leading-relaxed">
              Works determined to be predominantly AI-generated are immediately banned from the platform—a zero-tolerance policy unmatched in the industry.
            </p>
          </div>

          <div className="w-full rounded-lg overflow-hidden border border-notion-border mb-8">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/c7bfdb061ce54b922bcf65d2dbbe3be3.png"
              alt="HumanMarq Analysis Results"
              title="HumanMarq Beta AI Analysis Interface"
              className="w-full h-auto object-contain"
            />
          </div>

          <div className="w-full rounded-lg overflow-hidden border border-notion-border">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/c256c18860827e05919f0eda005a9ab5.png"
              alt="HumanMarq Detection Flow"
              title="HumanMarq AI Detection Interface"
              className="w-full h-auto object-contain"
            />
          </div>
        </section>

        {/* Designing the HumanMarq Score */}
        <section id="score-system" className="mb-20">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-8">
            Turning Detection Into Trust
          </h2>

          {/* The Score System */}
          <div className="mb-12">
            <h3 className="text-[20px] font-medium text-notion-text mb-6">The Score System</h3>
            <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
              HumanMarq ranges across a spectrum:
            </p>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-heart-line text-green-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-[14px] font-medium text-notion-text">Fully Human-Created</p>
                  <p className="text-[12px] text-notion-secondary">95-100% Human-Likelihood</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-smile-line text-blue-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-[14px] font-medium text-notion-text">Human-Assisted</p>
                  <p className="text-[12px] text-notion-secondary">70-94% Human-Likelihood</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-robot-2-line text-yellow-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-[14px] font-medium text-notion-text">AI-Assisted</p>
                  <p className="text-[12px] text-notion-secondary">40-69% Human-Likelihood</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-spam-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-[14px] font-medium text-notion-text">Predominantly AI-Generated</p>
                  <p className="text-[12px] text-notion-secondary">0-39% Human-Likelihood</p>
                </div>
              </div>
            </div>

            <p className="text-[15px] text-notion-secondary leading-relaxed italic">
              A spectrum builds credibility instead of binary labeling — it acknowledges nuance while maintaining clear standards.
            </p>
          </div>

          {/* Score Visualization */}
          <div className="mb-12">
            <h3 className="text-[20px] font-medium text-notion-text mb-6">Score Visualization</h3>
            <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
              Design decisions focused on clarity and trust:
            </p>

            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6 mb-6">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-notion-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-medal-line text-notion-accent text-xs"></i>
                  </span>
                  <span className="text-[14px] text-notion-text">Clean badge system</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-notion-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-number-1 text-notion-accent text-xs"></i>
                  </span>
                  <span className="text-[14px] text-notion-text">Percentage displayed prominently</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-notion-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-notion-accent text-xs"></i>
                  </span>
                  <span className="text-[14px] text-notion-text">Subtext explaining "Based on automated analysis for transparency"</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-notion-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-chat-smile-3-line text-notion-accent text-xs"></i>
                  </span>
                  <span className="text-[14px] text-notion-text">Non-accusatory language</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-notion-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-book-open-line text-notion-accent text-xs"></i>
                  </span>
                  <span className="text-[14px] text-notion-text">Educational tone, not punitive</span>
                </li>
              </ul>
            </div>

            <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-notion-sidebar">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/b3229c457fbdfa149bab1f70512b9ec5.png"
                alt="HumanMarq Score UI"
                title="HumanMarq Score Visualization"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* Iconography & States */}
          <div className="mb-12">
            <h3 className="text-[20px] font-medium text-notion-text mb-6">Iconography & States</h3>
            <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
              A complete system was built to support various states:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-notion-sidebar border border-notion-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-shield-star-line text-notion-accent"></i>
                  </span>
                  <h4 className="text-[14px] font-medium text-notion-text">Authenticity Badges</h4>
                </div>
                <p className="text-[13px] text-notion-secondary">Visual indicators for each score tier</p>
              </div>
              <div className="bg-notion-sidebar border border-notion-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-pulse-line text-notion-accent"></i>
                  </span>
                  <h4 className="text-[14px] font-medium text-notion-text">Confidence Indicators</h4>
                </div>
                <p className="text-[13px] text-notion-secondary">Transparency about detection certainty</p>
              </div>
              <div className="bg-notion-sidebar border border-notion-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-question-answer-line text-notion-accent"></i>
                  </span>
                  <h4 className="text-[14px] font-medium text-notion-text">Dispute Pathways</h4>
                </div>
                <p className="text-[13px] text-notion-secondary">Clear process for seller appeals</p>
              </div>
              <div className="bg-notion-sidebar border border-notion-border rounded-lg p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-forbid-line text-red-500"></i>
                  </span>
                  <h4 className="text-[14px] font-medium text-notion-text">Ban State</h4>
                </div>
                <p className="text-[13px] text-notion-secondary">For predominantly AI-generated work</p>
              </div>
            </div>

            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <p className="text-[15px] text-notion-text leading-relaxed">
                <span className="font-medium">Tone balance:</span> Firm but not aggressive. Transparent but not absolute.
              </p>
            </div>
          </div>
        </section>

        {/* Platform Policy Integration */}
        <section id="seller-experience" className="mb-20">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-8">
            Product + Policy Working Together
          </h2>
          
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            HumanMarq wasn't just UI. It required a complete ecosystem approach.
          </p>

          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6 mb-8">
            <p className="text-[16px] text-notion-text leading-relaxed mb-3">
              "We're not anti-technology—we're pro-artist. But when AI tools are used to deceive collectors and undermine artists who've spent decades honing their craft, someone needs to stand up. We're that someone."
            </p>
            <p className="text-[14px] text-notion-secondary">
              — Chris Tung, Co-Founder of Raremarq
            </p>
          </div>

          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            Implementation required:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-5">
              <div className="w-8 h-8 rounded-full bg-notion-accent/10 flex items-center justify-center mb-3">
                <i className="ri-radar-line text-notion-accent text-sm"></i>
              </div>
              <h4 className="text-[14px] font-medium text-notion-text mb-2">Backend Detection Signals</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                AI analysis infrastructure — we built this with Claude AI, SightEngine, and many other AI training models
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-5">
              <div className="w-8 h-8 rounded-full bg-notion-accent/10 flex items-center justify-center mb-3">
                <i className="ri-eye-line text-notion-accent text-sm"></i>
              </div>
              <h4 className="text-[14px] font-medium text-notion-text mb-2">Public-Facing Score Display</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                We iterated on many icons and badge styles as shown above
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-5">
              <div className="w-8 h-8 rounded-full bg-notion-accent/10 flex items-center justify-center mb-3">
                <i className="ri-graduation-cap-line text-notion-accent text-sm"></i>
              </div>
              <h4 className="text-[14px] font-medium text-notion-text mb-2">Seller Education</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                We posted blog post, <a href="#" className="text-notion-accent hover:underline">view blog post here</a> (coming soon)
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-5">
              <div className="w-8 h-8 rounded-full bg-notion-accent/10 flex items-center justify-center mb-3">
                <i className="ri-scales-line text-notion-accent text-sm"></i>
              </div>
              <h4 className="text-[14px] font-medium text-notion-text mb-2">Enforcement Rules</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                Consistent policy application
              </p>
            </div>
          </div>
        </section>

        {/* Business & Market Impact */}
        <section id="impact" className="mb-20">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-8">
            Industry-Wide Implications
          </h2>
          
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-8">
            The HumanMarq system set a new precedent that could force other marketplaces to follow suit or risk losing credibility with serious collectors.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <div className="w-10 h-10 rounded-full bg-notion-accent/10 flex items-center justify-center mb-4">
                <i className="ri-star-line text-notion-accent text-lg"></i>
              </div>
              <h4 className="text-[15px] font-medium text-notion-text mb-2">Differentiated Platform</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                Set Raremarq apart from open marketplaces
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <div className="w-10 h-10 rounded-full bg-notion-accent/10 flex items-center justify-center mb-4">
                <i className="ri-vip-crown-line text-notion-accent text-lg"></i>
              </div>
              <h4 className="text-[15px] font-medium text-notion-text mb-2">Curated Identity</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                Reinforced premium platform positioning
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <div className="w-10 h-10 rounded-full bg-notion-accent/10 flex items-center justify-center mb-4">
                <i className="ri-team-line text-notion-accent text-lg"></i>
              </div>
              <h4 className="text-[15px] font-medium text-notion-text mb-2">Creator Relationships</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                Strengthened trust with artists including Stanley "Artgerm" Lau, Frank Cho, and other industry luminaries
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <div className="w-10 h-10 rounded-full bg-notion-accent/10 flex items-center justify-center mb-4">
                <i className="ri-shield-user-line text-notion-accent text-lg"></i>
              </div>
              <h4 className="text-[15px] font-medium text-notion-text mb-2">Buyer Confidence</h4>
              <p className="text-[13px] text-notion-secondary leading-relaxed">
                Increased trust in purchases through transparent authenticity scoring
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 text-notion-text rounded-lg p-6">
            <p className="text-[16px] font-medium leading-relaxed text-center">
              Category defense, not just feature launch.
            </p>
          </div>

          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6 mb-8">
            <p className="text-[15px] text-notion-text leading-relaxed mb-3">
              Every new listing now undergoes HumanMarq review. Sellers attempting to pass off AI-generated work as human-created face immediate delisting and violation notices.
            </p>
            <p className="text-[15px] text-notion-text font-medium leading-relaxed">
              Combined with Raremarq's deeper integration with CGC (Certified Guaranty Company), the platform is positioning itself as the gold standard for authenticity in the digital art age.
            </p>
          </div>

          <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-notion-sidebar">
            <div className="w-full h-[400px] flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/09b2e855b8366559d31cbd26dbde2adf.png"
                alt="HumanMarq Launch Graphic"
                title="HumanMarq Business Impact"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </section>

        {/* Results & Outcomes */}
        <section id="results" className="mb-20">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-8">
            What Success Looked Like
          </h2>
          
          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-4 p-5 bg-green-50 border border-green-200 rounded-lg">
              <span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-green-600 text-sm"></i>
              </span>
              <div>
                <h4 className="text-[15px] font-medium text-notion-text mb-1">Clearer Buyer Confidence Signals</h4>
                <p className="text-[13px] text-notion-secondary leading-relaxed">
                  Buyers could make informed decisions with visible authenticity scores
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-green-50 border border-green-200 rounded-lg">
              <span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-green-600 text-sm"></i>
              </span>
              <div>
                <h4 className="text-[15px] font-medium text-notion-text mb-1">Reduced AI-Related Disputes</h4>
                <p className="text-[13px] text-notion-secondary leading-relaxed">
                  Transparent scoring reduced confusion and conflict
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-green-50 border border-green-200 rounded-lg">
              <span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-green-600 text-sm"></i>
              </span>
              <div>
                <h4 className="text-[15px] font-medium text-notion-text mb-1">Strong Creator Alignment</h4>
                <p className="text-[13px] text-notion-secondary leading-relaxed">
                  Artists appreciated the platform's commitment to protecting human work
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-5 bg-green-50 border border-green-200 rounded-lg">
              <span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-green-600 text-sm"></i>
              </span>
              <div>
                <h4 className="text-[15px] font-medium text-notion-text mb-1">Elevated Platform Integrity</h4>
                <p className="text-[13px] text-notion-secondary leading-relaxed">
                  Raremarq positioned as a trusted, quality-focused marketplace
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 text-notion-text rounded-lg p-6">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-money-dollar-circle-line text-blue-600 text-sm"></i>
              </span>
              <div>
                <h4 className="text-[15px] font-medium text-notion-text mb-2">Future Monetization Opportunity</h4>
                <p className="text-[14px] text-notion-secondary leading-relaxed">
                  We believe there's a large monetization opportunity to leverage HumanMarq as a stamp of approval for authentic listings in the near future.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reflection */}
        <section id="reflection" className="mb-20">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-8">
            Reflection
          </h2>
          
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8">
            <p className="text-[15px] text-notion-text leading-relaxed">
              HumanMarq represents a pivotal moment in how platforms can take a stand for authenticity. By making trust visible and actionable, we created a system that protects both collectors and artists in an era of increasing AI sophistication.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mb-20">
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-10">
            <div className="text-center">
              <h3 className="font-serif text-2xl font-medium text-notion-text mb-6">
                Explore More Projects
              </h3>
              <button
                onClick={handleBackToPortfolio}
                className="inline-flex items-center gap-2 px-6 py-3 bg-notion-accent text-white rounded-lg text-[14px] font-medium hover:bg-notion-accent/90 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Portfolio
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="border-t border-notion-border bg-notion-sidebar">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <Link
                to="/"
                className="font-serif text-[20px] font-medium text-notion-text hover:text-notion-accent transition-colors cursor-pointer"
              >
                annie
              </Link>
              <p className="text-[13px] text-notion-secondary mt-2">
                Product Designer &amp; UX Strategist
              </p>
            </div>
            <div className="flex items-center gap-6">
              <a
                href="mailto:ading@ucdavis.edu"
                className="text-[13px] text-notion-secondary hover:text-notion-accent transition-colors cursor-pointer"
              >
                ading@ucdavis.edu
              </a>
              <a
                href="https://www.linkedin.com/in/annieding04/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13px] text-notion-secondary hover:text-notion-accent transition-colors cursor-pointer"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
